<template>
  <div>
    <div
      v-if="shouldShowRegeneratePromptBlock"
      class="pdp-prompt-regenerate mb-3"
    >
      <div class="pdp-prompt-regenerate__body">
        <div class="pdp-prompt-regenerate__icon">
          <i class="bi bi-arrow-clockwise" />
        </div>

        <div class="pdp-prompt-regenerate__content">
          <div class="pdp-prompt-regenerate__title">
            This report was generated with a different prompt
          </div>

          <div class="pdp-prompt-regenerate__text">
            Current report prompt:
            <strong>{{ currentReportPromptLabel }}</strong>.
            Selected prompt:
            <strong>{{ selectedPromptLabel }}</strong>.
          </div>
        </div>
      </div>

      <button
        type="button"
        class="btn btn-success btn-sm pdp-prompt-regenerate__btn"
        :disabled="isRegenerating"
        @click="$emit('regenerate')"
      >
        <i
          class="bi"
          :class="isRegenerating ? 'bi-hourglass-split' : 'bi-arrow-clockwise'"
        />
        {{ isRegenerating ? 'Regenerating...' : 'Re-generate with selected prompt' }}
      </button>
    </div>

    <DefaultTemplate
      v-if="isDefaultTemplate"
      :report-data="reportData"
      :analysis-content="analysisContent"
    />

    <ContentTemplate
      v-else-if="isContentTemplate"
      :analysis-content="analysisContent"
      :images="reportData.images"
    />
  </div>
</template>

<script>
import DefaultTemplate from "@frontend/components/common/dashboard/pdp-tabs/DefaultTemplate.vue";
import ContentTemplate from "@frontend/components/common/dashboard/pdp-tabs/ContentTemplate.vue";

const DEFAULT_PROMPT_VALUE = 'default';

export default {
  name: 'PdpTextCopyTab',

  components: {
    DefaultTemplate,
    ContentTemplate,
  },

  props: {
    reportData: {
      type: Object,
      default: null,
    },

    analysisContent: {
      type: [Object, String, Array],
      default: () => ({}),
    },

    reportContent: {
      type: Object,
      default: null,
    },

    selectedPromptValue: {
      type: [String, Number, null],
      default: DEFAULT_PROMPT_VALUE,
    },

    selectedPromptLabel: {
      type: String,
      default: 'Default Prompt',
    },

    isRegenerating: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isDefaultTemplate() {
      return (
        this.analysisContent &&
        typeof this.analysisContent === 'object' &&
        !Array.isArray(this.analysisContent)
      );
    },

    isContentTemplate() {
      return typeof this.analysisContent === 'string';
    },

    normalizedSelectedPromptValue() {
      if (
        this.selectedPromptValue === null ||
        this.selectedPromptValue === undefined ||
        this.selectedPromptValue === ''
      ) {
        return DEFAULT_PROMPT_VALUE;
      }

      return String(this.selectedPromptValue);
    },

    currentReportPromptValue() {
      if (!this.reportContent) {
        return null;
      }

      if (
        this.reportContent.custom_prompt_id === null ||
        this.reportContent.custom_prompt_id === undefined ||
        this.reportContent.custom_prompt_id === ''
      ) {
        return DEFAULT_PROMPT_VALUE;
      }

      return String(this.reportContent.custom_prompt_id);
    },

    currentReportPromptLabel() {
      if (!this.reportContent) {
        return 'No report generated';
      }

      if (this.currentReportPromptValue === DEFAULT_PROMPT_VALUE) {
        return 'Default Prompt';
      }

      return `Custom Prompt #${this.currentReportPromptValue}`;
    },

    shouldShowRegeneratePromptBlock() {
      if (!this.reportData) {
        return false;
      }

      if (!this.reportContent) {
        return true;
      }

      return this.currentReportPromptValue !== this.normalizedSelectedPromptValue;
    },
  },
};
</script>

<style scoped lang="scss">
.pdp-prompt-regenerate {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
}

.pdp-prompt-regenerate__body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.pdp-prompt-regenerate__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #fef3c7;
  color: #b45309;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pdp-prompt-regenerate__content {
  min-width: 0;
}

.pdp-prompt-regenerate__title {
  font-size: 14px;
  line-height: 1.3;
  font-weight: 800;
  color: #92400e;
  margin-bottom: 3px;
}

.pdp-prompt-regenerate__text {
  font-size: 13px;
  line-height: 1.35;
  color: #78350f;
}

.pdp-prompt-regenerate__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  i::before {
    vertical-align: top;
  }
}

@media (max-width: 768px) {
  .pdp-prompt-regenerate {
    align-items: stretch;
    flex-direction: column;
  }

  .pdp-prompt-regenerate__btn {
    justify-content: center;
  }
}
</style>
